import { Injectable } from "@angular/core";
// Legacy build imported by explicit path: webpack 4 cannot parse the modern build
// and ignores the package exports map. See src/typings.d.ts for the full reasoning.
import { RudderAnalytics } from "@rudderstack/analytics-js/dist/npm/legacy/cjs/index.cjs";
import { AppSettings } from "../app-settings";

/**
 * Console tag for every line this module emits.
 *
 * App-prefixed ON PURPOSE. These apps embed one another in iframes, and an
 * iframe's console output lands in the SAME console as its parent with
 * nothing to say which document produced it. A shared `[Analytics]` tag is
 * therefore unattributable exactly when attribution matters - while
 * verifying that each app initialized its own client. The `-RudderStack`
 * suffix is shared with the other apps so `[*-RudderStack]` matches them all.
 */
const LOG_TAG = "[lms-RudderStack]";
/**
 * The app's analytics client. RudderStack is the ONLY destination.
 *
 * It replaced two Segment paths: `push_event_properties` (the CDN global that
 * POSTed to the JSEvents-SEG lambda) and the JSEvent lambda that seven
 * components POSTed to directly. Both are gone; EventService is the single seam
 * in front of this service.
 *
 * Follows the house RudderStack client pattern established in prism-learning-ui
 * (apps/prism-prep-ui/src/components/analytics/rudderstack.js) and oq-connect-ui:
 * the @rudderstack/analytics-js npm package, `new RudderAnalytics()` +
 * `.load(writeKey, dataPlaneUrl)`, returning a no-op when unconfigured.
 *
 * Angular-side concerns mirror CustomerIoService:
 *  - Fully gated behind AppSettings.rudderWriteKey. With no key every method is a
 *    complete no-op (ship-dark safe).
 *  - Every public method is wrapped in try/catch. Analytics must never surface to
 *    the user.
 *
 * PAYLOAD CONTRACT - decided deliberately, do not "fix" any of this. It was
 * modelled on what the JSEvents-SEG lambda sent, including behaviour that looks
 * wrong, so that history stays comparable:
 *
 *   userId:       userIdentity.toLowerCase()   <- ALWAYS userId, even when the
 *                                                identity is an anonymous UUID.
 *                                                Creates an identified profile per
 *                                                anonymous visitor. On purpose.
 *   timestamp:    properties['eg-timestamp']
 *   properties:   { ...computedProperties }
 *   integrations: isValidEmail(identity) ? {All:true}
 *                                        : {All:true, 'Customer.io': false}
 *
 * Two places where this deliberately does NOT match what the old lambdas sent:
 *
 *  1. Identity resolution. The lambda used `analytics.user().id() ?? anonymousId()`.
 *     We prefer the app's own user email, then userLoginCookie, and only then an
 *     anonymous id (see resolveUserIdentity). That is a better value.
 *
 *  2. The old JSEvent lambda sent NO `integrations` field, so anonymous users'
 *     events reached Customer.io. We send `integrations` on every event, so those
 *     ~120/day are suppressed for anonymous identities like everything else.
 */
/**
 * Parent domain for RudderStack's identity cookies.
 *
 * Derived from the hostname rather than configured, so it cannot drift between apps:
 * every app under *.e-gmat.com independently computes the same ".e-gmat.com". A config
 * field would let one typo silently re-silo one app, with no error raised anywhere -
 * and `setCookieDomain` is dropped without warning when it is not a string, so a
 * missing config value would look exactly like success.
 *
 * The derivation is also what makes QA work: the QA hosts are on e-gmattest.com, not
 * e-gmat.com, so a single hardcoded ".e-gmat.com" would be rejected by the browser on
 * every QA host and the change could never be verified before prod.
 *
 * Returns undefined for localhost and raw IPs, which leaves the SDK on its default
 * (current host only) - correct for local dev, where a ".e-gmat.com" cookie is
 * rejected by the browser outright.
 */
function parentCookieDomain(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const host = window.location.hostname;
  if (/^[\d.]+$/.test(host)) {
    return undefined; // raw IP - cannot set a domain cookie
  }
  const labels = host.split(".");
  if (labels.length < 2) {
    return undefined; // localhost - SDK default
  }
  return "." + labels.slice(-2).join("."); // lms.e-gmat.com -> .e-gmat.com
}

/**
 * Cookie set by blitz-admin-api's loginAs endpoint for the whole of an admin-driven
 * session ("Login as User" in bz-admin). It is the readable twin of loggedInAsUser,
 * which is HttpOnly and so invisible here.
 *
 * While it is present this service is a COMPLETE no-op - see init(). Analytics from
 * an admin poking around a student's account is wrong on its own terms, but the
 * reason this is a correctness fix rather than hygiene is identity:
 *
 * The SDK keeps ONE anonymousId per browser, shared across every *.e-gmat.com app by
 * setCookieDomain, and in an impersonated session it is the ADMIN's. track() sets
 * userId but does not override anonymousId, so each event carries the student's
 * userId next to the admin's anonymousId - a fresh alias for Mixpanel to merge on.
 * Impersonate three students in a row and the admin's device id is aliased to all
 * three, collapsing them into ONE profile. Same failure the reset() comment below
 * records for shared devices, and the same one the server-side pipeline avoids by
 * stripping stray anonymousIds (rudderstack/intercom-webhook/README.md).
 *
 * Suppressing at init() rather than minting a per-session anonymousId is deliberate:
 * an admin's clicks are not the student's behaviour, so the events should not exist.
 */
const IMPERSONATED_SESSION_COOKIE = "impersonatedSession";

/** One-time cleanup marker. Bump the suffix only if RL_COOKIE_KEYS changes. */
const RL_CLEANUP_FLAG = "rl_host_cookies_cleared_v1";

/**
 * The SDK's identity cookies, every one of which the pre-setCookieDomain build
 * wrote host-scoped.
 */
const RL_COOKIE_KEYS = [
  "rl_user_id",
  "rl_trait",
  "rl_anonymous_id",
  "rl_group_id",
  "rl_group_trait",
  "rl_session",
  "rl_page_init_referrer",
  "rl_page_init_referring_domain",
  "rl_auth_token",
];

/**
 * Delete the host-scoped identity cookies left by the pre-setCookieDomain build.
 *
 * Without this, a returning user's browser holds TWO cookies named rl_anonymous_id -
 * the old host-scoped one and the new parent-scoped one - and which one the SDK reads
 * is not something to rely on. In the bad case it keeps reading the stale host-scoped
 * value and the fix silently does nothing for exactly the users it was meant to unify.
 *
 * Omitting the domain attribute targets the HOST-scoped cookie only, leaving the new
 * parent-domain one intact - the two are distinct entries as far as the browser is
 * concerned. Verified on QA: a parent-scoped rl_user_id written by the public site
 * survives this cleanup untouched. Runs once per browser, guarded by a flag, because
 * it must not fight the cookies the SDK is about to write.
 */
function clearHostScopedRlCookies(): void {
  try {
    if (localStorage.getItem(RL_CLEANUP_FLAG)) {
      return;
    }
    RL_COOKIE_KEYS.forEach((k) => {
      document.cookie = k + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    });
    localStorage.setItem(RL_CLEANUP_FLAG, "1");
  } catch (e) {
    // Private mode / blocked storage: skip rather than loop the cleanup every load.
  }
}

@Injectable()
export class RudderStackService {
  private initialized = false;
  /** The SDK instance, or null while unconfigured / failed to load. */
  private rudder: RudderAnalytics = null;

  private get writeKey(): string {
    return AppSettings.rudderWriteKey || "";
  }
  private get dataPlaneUrl(): string {
    return AppSettings.rudderDataPlaneUrl || "";
  }
  /** Both key and dataplane are required; either missing -> complete no-op. */
  private get enabled(): boolean {
    return !!this.rudder;
  }

  /**
   * First 4 and last 4 characters only, for the init log.
   *
   * The write key is public — it ships in the bundle either way — but a full key
   * in the console is the kind of string that gets pasted into a ticket, so log
   * only enough to tell the QA source apart from the prod one at a glance. Short
   * or empty values collapse to a constant rather than leaking a large fraction
   * of themselves.
   */
  private maskKey(key: string): string {
    if (!key) {
      return "(none)";
    }
    if (key.length < 12) {
      return "(set)";
    }
    return key.slice(0, 4) + "..." + key.slice(-4);
  }

  /**
   * Dataplane URL with the host's leading labels elided.
   *
   * Masked from the FRONT, unlike maskKey: the tail is the half that identifies
   * the environment (...prismlearning.academy vs ...prismlearning.info), so
   * keeping first-4 here would print "http" and tell you nothing. Scheme is kept
   * so a misconfigured http:// dataplane is still visible.
   */
  private maskUrl(url: string): string {
    if (!url) {
      return "(none)";
    }
    const sep = url.indexOf("//");
    const scheme = sep === -1 ? "" : url.slice(0, sep + 2);
    const rest = sep === -1 ? url : url.slice(sep + 2);
    if (rest.length < 12) {
      return "(set)";
    }
    return scheme + "..." + rest.slice(-12);
  }

  /**
   * True while this browser is inside an admin "Login as User" session.
   *
   * Public so CustomerIoService gates on the SAME signal - two analytics clients
   * disagreeing about whether the session is real is exactly how one of them keeps
   * merging profiles after the other was fixed.
   */
  isImpersonatedSession(): boolean {
    return this.getCookie(IMPERSONATED_SESSION_COOKIE) === "1";
  }

  /**
   * Clear the impersonation marker, so the next REAL login in this browser is
   * tracked again.
   *
   * Called from the two paths that end such a session client-side (UserService
   * .logout, AppComponent.logoffExpert). The API clears it too, on its own logout;
   * this covers the admin who just walks out of the student's account.
   *
   * Deleted on the parent domain AND host-scoped, because which one the browser
   * holds depends on the app that set it, and missing the live one would leave
   * analytics silently off for that person.
   */
  endImpersonatedSession(): void {
    if (typeof window === "undefined") {
      return;
    }
    try {
      const expiry = "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
      document.cookie = IMPERSONATED_SESSION_COOKIE + expiry;
      const domain = parentCookieDomain();
      if (domain) {
        document.cookie =
          IMPERSONATED_SESSION_COOKIE + expiry + "; domain=" + domain;
      }
    } catch (e) {
      console.error(LOG_TAG + " failed to clear impersonation marker:", e);
    }
  }

  /**
   * Entry point. Called once from AppComponent.ngOnInit alongside
   * customerIoService.init(). No-op when unconfigured, which is how analytics
   * stays off in environments that have no RudderStack source yet.
   */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // Admin-driven session: load nothing. `rudder` stays null, so `enabled` is
    // false and EVERY public method below is already a no-op - no track, no page,
    // no identify, and no SDK to mint or rebind an anonymousId. See the constant.
    if (this.isImpersonatedSession()) {
      console.log(
        LOG_TAG +
          " impersonated session (bz-admin 'Login as User') - analytics disabled",
      );
      return;
    }

    if (!this.writeKey || !this.dataPlaneUrl) {
      console.error(
        LOG_TAG +
          " NOT CONFIGURED - no analytics will be sent" +
          " (writeKey=" +
          this.maskKey(this.writeKey) +
          ", dataPlaneUrl=" +
          this.maskUrl(this.dataPlaneUrl) +
          ")",
      );
      return;
    }
    try {
      const rudder = new RudderAnalytics();
      // Must run BEFORE load(), so it cannot race the cookies the SDK writes.
      clearHostScopedRlCookies();
      const cookieDomain = parentCookieDomain();
      rudder.load(
        this.writeKey,
        this.dataPlaneUrl,
        cookieDomain ? { setCookieDomain: cookieDomain } : undefined,
      );
      this.rudder = rudder;
      console.log(
        LOG_TAG +
          " client initialized (writeKey=" +
          this.maskKey(this.writeKey) +
          ", dataPlaneUrl=" +
          this.maskUrl(this.dataPlaneUrl) +
          // cookieDomain is logged because it is the ONLY way to tell from a
          // console whether this app is sharing identity or is still siloed.
          ", cookieDomain=" +
          (cookieDomain || "(SDK default - current host only)") +
          ")",
      );
    } catch (e) {
      console.error(LOG_TAG + " Failed to initialize RudderStack:", e);
      return;
    }
    try {
      this.identify();
    } catch (e) {
      console.error(LOG_TAG + " RudderStack initial identify failed:", e);
    }
  }

  /**
   * The single track path. `properties` is passed through untouched apart from the
   * source marker, so the property names (typos included: Accesed_Channel,
   * "Operating system") stay exactly as marketing reports expect them.
   *
   * `identityOverride` is the app's own user email, supplied by the callers that
   * already have one (the components that POST to the lambda themselves). It wins
   * over the SDK-persisted value — see resolveUserIdentity().
   *
   * There used to be a second method here mirroring the OLD JSEvent lambda, which
   * sends no `integrations` field. That omission let anonymous users' events reach
   * Customer.io while every other event suppressed them. Decided: those events now
   * behave like everything else, so the two paths are one.
   */
  track(eventName: string, properties: any, identityOverride?: string): void {
    if (!this.enabled) {
      return;
    }
    try {
      const r = this.rudder;
      const identity = this.resolveUserIdentity(identityOverride);
      const props = this.decorate(properties);
      r.track(eventName, props, this.buildOptions(identity, props));
    } catch (e) {
      console.error(LOG_TAG + " RudderStack track failed:", eventName, e);
    }
  }

  /** Mirror of analytics.page() / firePageLoadEvent(). */
  page(name?: string, properties?: any): void {
    if (!this.enabled) {
      return;
    }
    try {
      const r = this.rudder;
      const identity = this.resolveUserIdentity();
      const props = this.decorate(properties || {});
      const options = this.buildOptions(identity, props);
      if (name) {
        r.page(name, props, options);
      } else {
        r.page(props, options);
      }
    } catch (e) {
      console.error(LOG_TAG + " RudderStack page failed:", e);
    }
  }

  /**
   * Mirror of the old identificationBZ(): identify only for a VALID EMAIL, so we
   * do the same — identify call volume must match between the two streams.
   *
   * Uses resolveIdentifiedEmail(), the same steps 1-2 that resolveUserIdentity()
   * uses, so there is ONE place that decides who the user is. It deliberately does
   * NOT use the full resolveUserIdentity(): that falls through to an anonymous id,
   * and identify-ing a UUID would mint an identified profile per anonymous visitor
   * and inflate identify volume.
   */
  identify(identityOverride?: string): void {
    if (!this.enabled) {
      return;
    }
    try {
      const r = this.rudder;
      const email = this.resolveIdentifiedEmail(identityOverride);
      if (!email) {
        return;
      }
      r.identify(
        email,
        { email: email },
        {
          integrations: this.getIntegrations(email),
        },
      );
    } catch (e) {
      console.error(LOG_TAG + " RudderStack identify failed:", e);
    }
  }

  /**
   * The identified email, or "" for an anonymous visitor. Public so that
   * CustomerIoService identifies from the SAME source as RudderStack, now that
   * Segment's window.analytics (which it used to mirror) is no longer loaded.
   */
  identifiedEmail(identityOverride?: string): string {
    return this.resolveIdentifiedEmail(identityOverride);
  }

  /**
   * Clear identity on logout, preventing identity bleed on shared devices.
   *
   * MUST clear the anonymousId, and that is NOT the default. RudderStack's
   * reset() "resets all data EXCEPT anonymousId" - unlike Segment's old
   * analytics.reset(), which clears both. Leaving it means the next person to log
   * in on this browser inherits the previous user's anonymousId, both identities
   * bind to it, and Mixpanel merges two different people into one profile.
   *
   * Observed exactly that: signing up rs1@e-gmat.com after logging out of
   * abhishek@e-gmat.com merged the two in the RudderStack-fed Mixpanel project
   * while they stayed separate in the Segment-fed one.
   *
   * The `{ entries: { anonymousId: true } }` form is the current API; the boolean
   * argument does the same thing but is deprecated, so it is only a fallback for
   * SDK versions that do not understand the options object.
   */
  reset(): void {
    if (typeof window === "undefined" || !this.rudder) {
      return;
    }
    try {
      this.rudder.reset({ entries: { anonymousId: true } });
    } catch (e) {
      try {
        this.rudder.reset(true);
      } catch (e2) {
        console.error(LOG_TAG + " RudderStack reset failed:", e2);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Payload construction
  // ---------------------------------------------------------------------------

  /**
   * Adds the source marker and nothing else. Mirrors the "fired-from-lambda-SEG"
   * flag the lambda sets, so rows stay attributable during reconciliation.
   *
   * Copies rather than mutates, so the caller's object — which is the SAME object
   * the old pipeline sent — is never touched by anything we do here.
   */
  private decorate(properties: any): any {
    const props = Object.assign({}, properties || {});
    props["fired-from-rudderstack"] = true;
    return props;
  }

  /**
   * NOTE FOR QA (verification step 2/3): confirm against the live SDK that the
   * emitted payload really carries this userId. The mechanism for forcing a
   * per-call userId differs across RudderStack SDK v1/v2/v3 — if the network
   * payload shows a null userId, this is the single place to adjust. The
   * requirement is that userId matches the lambda.
   */
  private buildOptions(identity: string, props: any): any {
    const userId = (identity || "").toLowerCase();
    const options: any = {
      userId: userId,
      integrations: this.getIntegrations(userId),
    };
    // JSEvents-SEG sets timestamp from eg-timestamp; absent -> destination stamps
    // receipt time, same as the lambda.
    if (props && props["eg-timestamp"]) {
      options.originalTimestamp = props["eg-timestamp"];
    }
    return options;
  }

  /**
   * Verbatim port of the getIntegrationsForSegment function that was inside the lambda (and
   * of km_event_fire.js:649). Note it deliberately does NOT set KISSmetrics:false —
   * the lambda omits that key even though the client-side twin sets it.
   */
  private getIntegrations(identity: string): any {
    const integrations: any = {};
    integrations["All"] = true;
    if (!this.validateEmail(identity)) {
      integrations["Customer.io"] = false;
    }
    return integrations;
  }

  // ---------------------------------------------------------------------------
  // Identity helpers — ports of km_event_fire.js, kept behaviourally identical
  // ---------------------------------------------------------------------------

  /** Verbatim regex from km_event_fire.js:1209. Do not substitute another one. */
  private validateEmail(email: string): boolean {
    const re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }

  /**
   * Resolves the value that goes into `userId`.
   *
   * Deliberately does NOT touch `window.analytics`. userLoginCookie is OUR
   * cookie, set by our own login, and it was the origin the old pipeline
   * identified FROM - so reading it directly is both earlier and more accurate
   * than reading any SDK's cached copy of it.
   *
   * Order: app email -> userLoginCookie -> RudderStack anonymousId.
   */
  /**
   * The IDENTIFIED half of the identity chain - the only part identify() may use.
   *
   *   1. The app's own user email, when the caller has one. Most trustworthy: it
   *      comes from our own session, not from a third-party SDK.
   *   2. userLoginCookie (pipe-delimited, field index 2) - OUR cookie, set by our
   *      own login, and the origin the old pipeline identified FROM.
   *
   * Returns "" when there is no valid email - i.e. the visitor is anonymous.
   * Never falls through to an anonymous id: identify() must not mint an identified
   * profile for an anonymous visitor.
   */
  private resolveIdentifiedEmail(identityOverride?: string): string {
    try {
      const candidate = identityOverride || this.getLoginCookieEmail();
      return this.validateEmail(candidate) ? candidate.toLowerCase() : "";
    } catch (e) {
      return "";
    }
  }

  private resolveUserIdentity(identityOverride?: string): string {
    let identity = "";
    try {
      // 1-2. App email, then userLoginCookie - shared with identify().
      identity = this.resolveIdentifiedEmail(identityOverride);
    } catch (e) {
      identity = "";
    }

    // 3. RudderStack's own anonymousId - the anonymous path for logged-out
    //    visitors. This replaced a read of Segment's `ajs_anonymous_id` cookie,
    //    which existed only so anonymous events could be joined across the two
    //    streams during the dual write. With Segment gone there is nothing to
    //    join against, and that cookie stops being written at all.
    if (!identity) {
      identity = this.getRudderAnonymousId();
    }

    if (this.validateEmail(identity)) {
      identity = identity.toLowerCase();
    }
    return identity;
  }

  /** RudderStack's own anonymous id - the anonymous identity. */
  private getRudderAnonymousId(): string {
    try {
      const r: any = this.rudder;
      if (r && typeof r.getAnonymousId === "function") {
        return r.getAnonymousId() || "";
      }
    } catch (e) {
      /* fall through */
    }
    return "";
  }

  /**
   * The real identity source: userLoginCookie, pipe-delimited, field index 2.
   * Same read the Segment snippet in the (now removed) CDN script
   * analytics_codes_new.js did before calling analytics.identify().
   */
  private getLoginCookieEmail(): string {
    const raw = this.getCookie("userLoginCookie");
    if (!raw) {
      return "";
    }
    const parts = raw.split("|");
    return parts.length > 2 ? parts[2].trim() : "";
  }

  private getCookie(name: string): string {
    try {
      const escaped = name.replace(/([.*+?^${}()|[\]\\])/g, "\\$1");
      const match = document.cookie.match(
        new RegExp("(?:^|; )" + escaped + "=([^;]*)"),
      );
      return match ? decodeURIComponent(match[1]) : "";
    } catch (e) {
      return "";
    }
  }
}
